import React from 'react';
import { Building2, Target, Eye, UserCheck, Warehouse, MapPin, Mail, Phone } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { aboutContent, stats } from '../data/mock';

export const About = () => {
  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.pexels.com/photos/4484073/pexels-photo-4484073.jpeg"
            alt="Team"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">About Blue Bird</h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Transforming India's supply chain complexity into competitive advantage
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <div className="inline-block mb-6">
                <span className="bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-medium">
                  Our Story
                </span>
              </div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">
                Built to Fix India's Supply Chain Challenges
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                {aboutContent.story}
              </p>
              <div className="mt-8 p-6 bg-slate-50 rounded-xl border-l-4 border-orange-500">
                <p className="text-slate-700 font-medium mb-2">Founded by</p>
                <p className="text-2xl font-bold text-slate-900">{aboutContent.founder}</p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/7018649/pexels-photo-7018649.jpeg"
                alt="Warehouse Operations"
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-orange-500 text-white p-6 rounded-xl shadow-xl">
                <p className="text-4xl font-bold">500+</p>
                <p className="text-sm">Trusted Clients</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="border-2 border-cyan-200 hover:shadow-2xl transition-shadow">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-100 to-cyan-200 rounded-full flex items-center justify-center mb-6">
                  <Eye className="text-cyan-600" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Our Vision</h3>
                <p className="text-slate-600 leading-relaxed">
                  {aboutContent.vision}
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-orange-200 hover:shadow-2xl transition-shadow">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-orange-200 rounded-full flex items-center justify-center mb-6">
                  <Target className="text-orange-600" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Our Mission</h3>
                <p className="text-slate-600 leading-relaxed">
                  {aboutContent.mission}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Why Choose Blue Bird?
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              We bring expertise, infrastructure, and commitment to every partnership
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Card className="hover:shadow-xl transition-shadow border-2 border-slate-100">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-100 to-orange-200 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Building2 className="text-orange-600" size={36} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Integrated Solutions</h3>
                <p className="text-slate-600 leading-relaxed">
                  All logistics services under one roof — no vendor juggling, seamless coordination.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow border-2 border-slate-100">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-cyan-100 to-cyan-200 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Warehouse className="text-cyan-600" size={36} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Extensive Network</h3>
                <p className="text-slate-600 leading-relaxed">
                  27+ warehouses across 19+ states with 67,000+ successful shipments delivered.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow border-2 border-slate-100">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mx-auto mb-6">
                  <UserCheck className="text-green-600" size={36} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">End-to-End Ownership</h3>
                <p className="text-slate-600 leading-relaxed">
                  We take full responsibility for your supply chain — from pickup to delivery.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="py-16 bg-gradient-to-r from-slate-800 to-slate-900">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-cyan-400 mb-2">
                  {stat.value}
                </div>
                <div className="text-sm md:text-base text-slate-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Headquarters</h2>
              <p className="text-slate-600">Visit us or get in touch</p>
            </div>

            <Card className="border-2 border-slate-200">
              <CardContent className="p-8">
                <div className="grid md:grid-cols-3 gap-8">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                      <MapPin className="text-orange-600" size={28} />
                    </div>
                    <h4 className="font-bold text-slate-900 mb-2">Address</h4>
                    <p className="text-sm text-slate-600">
                      Room no: 265, 2nd Floor, "C" Block<br />
                      Chenoy Trade Center, Parklane<br />
                      Secunderabad - 500003
                    </p>
                  </div>

                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mb-4">
                      <Phone className="text-cyan-600" size={28} />
                    </div>
                    <h4 className="font-bold text-slate-900 mb-2">Phone</h4>
                    <a href="tel:+919573912643" className="text-sm text-slate-600 hover:text-orange-500 transition-colors">
                      +91 9573912643
                    </a>
                  </div>

                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                      <Mail className="text-green-600" size={28} />
                    </div>
                    <h4 className="font-bold text-slate-900 mb-2">Email</h4>
                    <a href="mailto:info@bbcs.express" className="text-sm text-slate-600 hover:text-orange-500 transition-colors">
                      info@bbcs.express
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};
